import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ChevronDown } from 'lucide-react'
import type { AppDispatch } from '../store'
import {
  projectDataApi,
  useGetUsageQuery,
  useGetDailyCreditUsageQuery,
  useGetCreditPacksQuery,
  useStartCreditTopUpMutation,
} from '../services/projectDataApi'
import { useSubscription } from '../hooks/useSubscription'
import { SCHEDULE_CALL_URL } from '../lib/stripe-config'
import { trackEvent } from '../lib/analytics'
import {
  BURN_RATE_WINDOW_DAYS,
  CREDIT_EQUIVALENCE,
  burnRateLabel,
  burnRatePerDay,
  creditMeterLevel,
  formatCreditCount,
  meterCta,
  meterFillPercent,
  packOfferLabel,
  pillLabel,
  planLine,
  preferredPack,
  runwayLabel,
  type CreditMeterAction,
  type CreditMeterLevel,
} from '../lib/creditMeter'
import './CreditMeter.css'

/** Everything the meter needs to say about an account, resolved before anything is rendered. */
export interface CreditMeterReading {
  level: CreditMeterLevel
  waived: boolean
  remaining: number
  allotment: number | null
  planKey: string | null
  /** False when the project is funded by someone else's plan, which changes what we may offer. */
  ownsAccount: boolean
  ownerName: string | null
  waiverEndsAt: string | null
  /** The viewer's own subscription renewal, or null when it isn't theirs to know. */
  renewsAt: number | null
  /** True when the balance is worth interrupting for — what promotes the meter to a mobile banner. */
  needsAttention: boolean
}

/**
 * The funding account's balance, polled while the tab is focused.
 *
 * Scoped to the team that pays for the project rather than to the viewer, because those differ: a
 * teammate spends the owner's credits, and showing them their own empty account would be a lie about
 * why a run stopped. Polled on the same 30s cadence as the billing graph, since nothing invalidates a
 * balance when a task spends it.
 */
export function useCreditMeterReading(teamId: string | null): CreditMeterReading | null {
  const usageQuery = useGetUsageQuery(teamId ?? undefined, {
    pollingInterval: 30_000,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
  })
  const { subscription } = useSubscription()
  const usage = usageQuery.data ?? null

  return useMemo(() => {
    if (!usage || typeof usage.available_credits !== 'number') return null
    const team = usage.teams?.find((entry) => entry.id === (usage.team_id ?? teamId)) ?? null
    const remaining = usage.available_credits
    const allotment = team?.monthly_credits ?? null
    const waived = usage.usage_waived === true
    const ownsAccount = team ? team.is_owner : true
    const level = creditMeterLevel(remaining, allotment)
    return {
      level,
      waived,
      remaining,
      allotment,
      planKey: team?.plan_key ?? null,
      ownsAccount,
      ownerName: team?.owner_name ?? null,
      waiverEndsAt: usage.usage_code_ends_at ?? null,
      renewsAt: ownsAccount ? subscription?.currentPeriodEnd ?? null : null,
      needsAttention: !waived && level !== 'healthy',
    }
  }, [usage, teamId, subscription])
}

// Leaving the SPA for Stripe. Module scope because react-compiler forbids a hook writing to anything
// declared outside it, and window.location is exactly that.
function leaveForStripe(url: string) {
  window.location.href = url
}

/**
 * Where Stripe should drop the buyer: back on the page they were reading, minus any leftover marker from
 * an earlier attempt, so a second try can't leave two `topup` params to disagree with each other.
 */
function currentReturnPath(): string {
  const url = new URL(window.location.href)
  url.searchParams.delete('topup')
  return `${url.pathname}${url.search}`
}

/**
 * Refresh the balance after a purchase.
 *
 * Credits are granted by Stripe's webhook, not by the redirect, so the money can land a moment after the
 * buyer is back. Rather than assert a number we don't have yet, this re-reads usage a few times over the
 * next few seconds; the meter's own 30s poll is the backstop if the webhook is slower than that.
 */
const TOPUP_REFRESH_DELAYS_MS = [0, 2_000, 5_000, 10_000]

function useCreditTopUpReturn() {
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const outcome = params.get('topup')
    if (!outcome) return

    const url = new URL(window.location.href)
    url.searchParams.delete('topup')
    window.history.replaceState(null, '', url.toString())

    if (outcome !== 'success') return
    trackEvent('credit-topup-returned', { outcome })
    const timers = TOPUP_REFRESH_DELAYS_MS.map((delay) =>
      window.setTimeout(() => dispatch(projectDataApi.util.invalidateTags([{ type: 'Usage', id: 'CURRENT' }])), delay),
    )
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [dispatch])
}

/**
 * A usage code's expiry, as the day it says it is.
 *
 * Formatted in UTC because these come back as UTC midnight (`…T00:00:00Z`), which west of Greenwich
 * renders as the previous day — telling someone their code dies a day before it does.
 */
function waiverEndLabel(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/**
 * The credit balance of the account funding this project, always visible while working in it.
 *
 * It exists to be seen BEFORE the credits run out: the out-of-credits modal and the creation gate both
 * arrive after the decision they hope to influence. Which is also why it stays quiet when there is
 * nothing to say — a healthy balance is a recessed pill with no call to action, and only a balance
 * worth acting on turns amber and grows a button.
 *
 * `variant` is placement, not decoration: `topbar` is the desktop pill, `compact` is the same pill
 * squeezed into the mobile header, and `banner` is the full-width mobile strip that a low balance earns.
 */
export default function CreditMeter({
  reading,
  variant,
  teamId = null,
  onOpenSettings,
}: {
  reading: CreditMeterReading
  variant: 'topbar' | 'compact' | 'banner'
  teamId?: string | null
  onOpenSettings: (tab: 'plan' | 'account') => void
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { level, waived, remaining, allotment } = reading
  useCreditTopUpReturn()

  // The history behind the burn rate is only worth fetching once someone asks for the detail.
  const range = useMemo(() => {
    const end = new Date()
    const start = new Date(end.getTime() - (BURN_RATE_WINDOW_DAYS - 1) * 86_400_000)
    const iso = (d: Date) => d.toISOString().slice(0, 10)
    return { start_date: iso(start), end_date: iso(end) }
  }, [])
  const historyQuery = useGetDailyCreditUsageQuery(
    { ...range, ...(teamId ? { team_id: teamId } : {}) },
    { skip: !open },
  )
  const burn = burnRatePerDay(historyQuery.data?.days ?? [])

  useEffect(() => {
    if (!open) return
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // A teammate can't buy anything for an account that isn't theirs, and a waiver means there is nothing
  // to sell: runs are already free. In both cases the panel explains instead of asking.
  const ownerCanBuy = reading.ownsAccount && !waived
  // Only tiers that would be sold a pack need the catalog, so a Free or Starter account never pays for
  // the request — their CTA is a plan either way.
  const packsRelevant = ownerCanBuy && meterCta(reading.planKey, { topUpAvailable: true }).action === 'topup'
  const packsQuery = useGetCreditPacksQuery(undefined, { skip: !packsRelevant })
  const pack = preferredPack(packsQuery.data?.packs)
  const [startTopUp, topUpState] = useStartCreditTopUpMutation()

  const cta = meterCta(reading.planKey, { topUpAvailable: pack !== null })
  // Held back for the one render where the catalog is still in flight, so the button doesn't say "Talk to
  // us" and then change its mind to "Top off credits" under the cursor.
  const showCta = ownerCanBuy && !(packsRelevant && packsQuery.isLoading)

  const toggle = useCallback(() => {
    setOpen((wasOpen) => {
      if (!wasOpen) trackEvent('credit-meter-opened', { level, waived, variant })
      return !wasOpen
    })
  }, [level, waived, variant])

  const runCta = useCallback(
    async (action: CreditMeterAction) => {
      trackEvent('upgrade-cta-clicked', {
        source: `credit-meter-${level}`,
        action,
        ...(action === 'topup' && pack ? { packCredits: pack.credits, packPriceId: pack.priceId } : {}),
      })
      if (action === 'contact') {
        setOpen(false)
        window.open(SCHEDULE_CALL_URL, '_blank', 'noopener,noreferrer')
        return
      }
      if (action === 'topup' && pack) {
        try {
          const { url } = await startTopUp({ priceId: pack.priceId, returnPath: currentReturnPath() }).unwrap()
          leaveForStripe(url)
        } catch (err) {
          // A checkout we couldn't start shouldn't dead-end the user: the plans page is somewhere real to
          // go, and it is where the other CTAs lead anyway.
          console.error('[credit-meter] top-up checkout failed:', err)
          setOpen(false)
          onOpenSettings('plan')
        }
        return
      }
      setOpen(false)
      onOpenSettings('plan')
    },
    [level, onOpenSettings, pack, startTopUp],
  )

  const state = waived ? 'waived' : level
  const fill = meterFillPercent(remaining, allotment)
  const label =
    variant === 'compact' && !waived && allotment
      ? `${formatCreditCount(Math.max(0, remaining))}/${formatCreditCount(allotment)}`
      : pillLabel({ level, remaining, allotment, waived })

  return (
    <div
      ref={containerRef}
      className={`credit-meter credit-meter--${variant} credit-meter--${state}`}
      data-testid="credit-meter"
      data-state={state}
    >
      <div className="credit-meter__group">
        <button
          type="button"
          className="credit-meter__pill"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={toggle}
          data-testid="credit-meter-trigger"
          data-analytics-event="credit-meter-trigger"
        >
          <span className="credit-meter__dot" aria-hidden />
          <span className="credit-meter__label">{label}</span>
          <ChevronDown size={14} strokeWidth={2} aria-hidden className="credit-meter__chevron" />
        </button>
        {/* Beside the pill rather than only inside the panel: once the balance matters, the shortest
            path to a plan shouldn't require opening anything. */}
        {showCta && state !== 'healthy' && (
          <button
            type="button"
            className="credit-meter__cta"
            onClick={() => runCta(cta.action)}
            disabled={topUpState.isLoading}
            data-testid="credit-meter-cta"
            data-analytics-event="credit-meter-cta"
          >
            {cta.label}
          </button>
        )}
      </div>

      {open && (
        <div className="credit-meter__panel" role="dialog" aria-label="Credits" data-testid="credit-meter-panel">
          <div className="credit-meter__panel-head">
            <span className="credit-meter__panel-label">Credits</span>
            <p className="credit-meter__headline">
              <strong className="credit-meter__headline-value">{formatCreditCount(Math.max(0, remaining))}</strong>
              <span>{allotment ? ` of ${formatCreditCount(allotment)} credits left` : ' credits left'}</span>
            </p>
            {fill !== null && (
              <div className="credit-meter__bar">
                <span className="credit-meter__bar-fill" style={{ width: `${fill}%` }} />
              </div>
            )}
            <p className="credit-meter__plan" data-testid="credit-meter-plan">
              {waived
                ? `Runs are free while your usage code is active${
                    reading.waiverEndsAt ? ` — until ${waiverEndLabel(reading.waiverEndsAt)}` : ''
                  }.`
                : planLine({ planKey: reading.planKey, renewsAt: reading.renewsAt, allotment })}
            </p>
            {!reading.ownsAccount && (
              <p className="credit-meter__owner" data-testid="credit-meter-owner">
                {reading.ownerName ? `${reading.ownerName}’s plan` : 'The team owner’s plan'} funds this project.
              </p>
            )}
          </div>

          <dl className="credit-meter__rows">
            <div className="credit-meter__row">
              <dt>1 credit</dt>
              <dd>≈ {CREDIT_EQUIVALENCE}</dd>
            </div>
            {allotment !== null && (
              <div className="credit-meter__row">
                <dt>Used to date</dt>
                <dd data-testid="credit-meter-used">
                  {formatCreditCount(Math.max(0, allotment - remaining))} / {formatCreditCount(allotment)}
                </dd>
              </div>
            )}
            {!waived && burnRateLabel(burn) && (
              <div className="credit-meter__row">
                <dt>Burn rate</dt>
                <dd data-testid="credit-meter-burn">{burnRateLabel(burn)}</dd>
              </div>
            )}
            {!waived && runwayLabel(remaining, burn) && (
              <div className="credit-meter__row">
                <dt>At this rate</dt>
                <dd className="credit-meter__runway" data-testid="credit-meter-runway">
                  {runwayLabel(remaining, burn)}
                </dd>
              </div>
            )}
          </dl>

          {showCta && (
            <>
              <button
                type="button"
                className="credit-meter__panel-cta"
                onClick={() => runCta(cta.action)}
                disabled={topUpState.isLoading}
                data-testid="credit-meter-panel-cta"
                data-analytics-event="credit-meter-panel-cta"
              >
                {topUpState.isLoading ? 'Opening checkout…' : cta.label}
              </button>
              {/* What the button charges, said before it is pressed rather than on Stripe's page. */}
              {cta.action === 'topup' && pack && (
                <p className="credit-meter__cta-note" data-testid="credit-meter-pack">
                  {packOfferLabel(pack)}, charged once
                </p>
              )}
            </>
          )}
          <button
            type="button"
            className="credit-meter__link"
            onClick={() => {
              setOpen(false)
              onOpenSettings('account')
            }}
            data-testid="credit-meter-history"
            data-analytics-event="credit-meter-history"
          >
            View usage history
          </button>
        </div>
      )}
    </div>
  )
}
